<script setup lang="ts">
import LeArticleGender from "./LeArticleGender.vue";
import LeArticleWordClass from "./LeArticleWordClass.vue";
import type { ClausensArticle } from "./types";

const props = defineProps<{
  article: ClausensArticle;
}>();
</script>

<template>
  <ul class="le-article-body-expanded">
    <template v-for="meaning in props.article.meanings" :key="meaning.meaningId">
      <li
        v-for="translation in meaning.translations"
        :key="translation.translationId"
        class="le-article-body-expanded__translation"
      >
        <span v-if="translation.combination" title="Kombination" class="le-article-body-expanded__combination">
          {{ translation.combination }} &raquo;
        </span>
        <span v-if="translation.categories" title="Kategori" class="le-article-body-expanded__categories">
          [{{ translation.categories.replaceAll(",", ", ") }}]
        </span>
        <span
          v-if="translation.langVariation"
          title="Sproglig variation"
          class="le-article-body-expanded__lang-variation"
        >
          ({{ translation.langVariation.replace("US", "Amerikansk") }})
        </span>
        <span v-if="translation.explanation" title="Forklaring" class="le-article-body-expanded__explanation">
          ({{ translation.explanation }})
        </span>

        <span class="le-article-body-expanded__translation-text">
          {{ translation.translation }}
        </span>

        <LeArticleWordClass
          v-if="translation.wordclass"
          class="le-article-body-expanded__word-class"
          :word-class="translation.wordclass"
        />
        <LeArticleGender
          v-if="translation.gender"
          class="le-article-body-expanded__gender"
          :gender="translation.gender"
        />
        <span
          v-if="translation.gramComment"
          title="Grammatisk kommentar"
          class="le-article-body-expanded__gram-comment"
        >
          ({{ translation.gramComment }})
        </span>
        <span v-if="translation.example" title="Eksempel" class="le-article-body-expanded__example">
          &ndash; {{ translation.example }}</span
        >
        <span v-if="translation.link" class="le-article-body-expanded__link-container">
          <span>se </span>
          <slot name="link" v-bind="{ word: translation.link }">
            <span class="le-article-body-expanded__placeholder-link">
              {{ translation.link }}
            </span>
          </slot>
        </span>
      </li>
    </template>
  </ul>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";
@import "@/assets/styles/dictsys-article";

.le-article-body-expanded {
  margin-left: -16px;
  list-style-position: outside;
  font-size: 1rem;

  #media-breakpoint-up(md, {
    margin-left: -8px;
  });

  &__translation {
    padding: 2px 4px;
    border-radius: 4px;

    &:hover {
      background-color: @colors[@secondary][100];
    }

    &::before {
      content: "";
      margin-left: -4px;
    }
  }

  &__combination,
  &__lang-variation,
  &__explanation,
  &__translation-text,
  &__word-class,
  &__gender,
  &__gram-comment,
  &__example {
    margin-right: 4px;

    #media-breakpoint-up(md, {
      margin-right: 6px;
    });
  }

  &__combination {
    color: rgb(0, 46, 166);
    font-style: italic;
  }

  &__categories,
  &__explanation {
    color: @colors[@primary][700];
    font-style: italic;
  }

  &__translation-text {
    font-weight: bold;
  }

  &__word-class,
  &__gender {
    color: @colors[@primary][700];
  }

  &__gender {
    bottom: -0.25em;
    margin-left: -4px;
    font-size: 1rem;
  }

  &__example {
    margin-left: -2px;
  }

  &__link-container {
    :slotted(a),
    .le-article-body-expanded__placeholder-link {
      .link();
      color: @colors[@primary][700];
      text-decoration: underline;
      cursor: pointer;

      &.focus-visible,
      &:hover {
        color: @colors[@primary][700];
      }
    }
  }
}
</style>
